import { FunctionComponent, useLayoutEffect, useState } from 'react';
import { Navigate, Route, Router as ReactRouter, Routes } from 'react-router-dom';
import { BrowserHistory } from 'history';
import history from 'src/router/lib/history';
import AuthenticationContainer from '../../../containers/authentication-container';
import AppContainer from '../../../modules/iam-login-flow/containers/iam-login-flow-callback-container';
import { RouterHookParams } from '../../../types';
import { defaultHomeRoute, RoutesConfig } from '../../lib/routes-config';
import { rfc6570ToReactRouterScheme } from '../../lib/url-builder';
import ProtectedRoute from '../protected-route';
interface RouterProps {
  loggedIn: boolean;
  routes: RoutesConfig;
  onEnterRoute: (params: RouterHookParams) => void;
}

interface CustomHistoryRouterProps {
  history: BrowserHistory;
}
/**
 * This CustomHistoryRouter is our alternative approach to the (undocumented) HistoryRouter
 * in v6 which is exported as 'unsafe' and could be removed from library anytime.
 * https://github.com/remix-run/react-router/blob/v6.3.0/packages/react-router-dom/index.tsx#L217-L243
 * Since this is build on the official api of the Router component, we should be better off
 * with our wrapper.
 *
 * In the long run we should the pattern of using hooks for navigation, which means to refactor
 * our business logic and remove all redirects/navigation calls from our sagas!
 *
 */
const CustomHistoryRouter: FunctionComponent<CustomHistoryRouterProps> = props => {
  const { history, ...rest } = props;

  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect(() => history.listen(setState), [history]);

  return <ReactRouter {...rest} location={state.location} navigationType={state.action} navigator={history} />;
};

const Router: FunctionComponent<RouterProps> = props => {
  const { routes, loggedIn, onEnterRoute } = props;

  return (
    // TODO replace custom router with BrowserRouter after getting rid of all
    // navigation logic (using history) outside of components (inside of sagas)
    <CustomHistoryRouter history={history}>
      <Routes>
        <Route path="/login" element={<AuthenticationContainer />} />
        {Object.keys(routes).map(routeName => {
          const path = rfc6570ToReactRouterScheme(routes[routeName].path);
          // Make 100% all paths start with a slash
          const pathWithSlash = path.startsWith('/') ? path : `/${path}`;
          const skipAuthentication = Boolean(routes[routeName].skipAuthentication);
          const currentComponent = routes[routeName].component;
          return (
            <Route
              path={pathWithSlash}
              key={`route-${routeName}`}
              element={
                <ProtectedRoute
                  loggedIn={loggedIn}
                  onEnterRoute={onEnterRoute}
                  routeName={routeName}
                  path={pathWithSlash}
                  skipAuthentication={skipAuthentication}
                >
                  <AppContainer currentComponent={currentComponent} />
                </ProtectedRoute>
              }
            />
          );
        })}
        <Route path="*" element={<Navigate to={defaultHomeRoute} />} />
      </Routes>
    </CustomHistoryRouter>
  );
};

export default Router;
